import { batch, signal } from "@preact/signals-react"


export const retailers = signal([])
// Sections of the picked retailer, for the Section picker. Empty while no
// retailer is picked: section ids are per retailer.
export const sections = signal([])
export const items = signal([])
export const counts = signal({ needs_qa: 0, validated: 0 })
export const total = signal(0)

export const selectedRetailerId = signal(null)
export const selectedSectionId = signal(null)
export const selectedCountry = signal('')   // ISO-2, '' = all
export const selectedKind = signal(null)
// Identity family (R1): product / logo / media. Single-select, because a
// resource belongs to exactly one — if it looks like it belongs to two,
// that is a data error and the back flags it with `family_mixed`.
// Distinct from `selectedKind` / `selectedParentKinds`, which describe
// WHERE a sighting appeared and live on the occurrence.
export const selectedFamily = signal(null)
// Multi-select on parent_kind via clickable chips in the filter bar.
// Empty array means "no filter" (show all). When non-empty, the list
// is sent to the back as a comma-separated `parent_kind=` param.
export const selectedParentKinds = signal([])
// Multi-select on intent (unified "what is this creative trying to
// communicate" slot — promotion / news / brand / editorial / bestseller
// / category / other). Same comma-separated wire format.
export const selectedIntents = signal([])
// Multi-select on composition (company / brand / product axis — see
// resource_match_svc.COMPOSITIONS).
export const selectedCompositions = signal([])
// Quick chip filters: categories (skincare / fragrances / makeup / …)
// and a curated short-list of common brands (Dior / Rabanne / Carolina
// Herrera / …). Both go to the back as comma-separated CSV which the
// list endpoint OR's together at query time.
export const selectedCategories = signal([])
// Brands are selected by `id_brand` (numbers), not by name: names cannot tell
// "Rabanne" from "Paco Rabanne", and accents never match because the JSON
// comparison is utf8mb4_bin, so "Lancome" would never find "Lancôme".
export const selectedBrands     = signal([])
// The brands actually present in the current slice, counted and ordered by the
// back: [{id_brand, name, n}, …]. Replaces the hand-written list of 12 that
// could not possibly match a 3.238-brand catalogue.
export const brandFacets = signal([])
// Accumulated {id_brand: name}, merged and never overwritten. A selected brand
// can vanish from the facets when the rest of the filters change (the GROUP BY
// emits no zero rows), and there is no other source of names on this page — the
// brand catalogue is only fetched inside the resource editor. Without this the
// active chip would render blank.
export const brandNames = signal({})
export const brandsExpanded = signal(false)
// Raw brands the matcher could not resolve to an id at all. A count, not a
// filter — distinct from the UNKNOWN BRAND facet, which is a real chip.
export const brandsUnresolved = signal(0)
export const selectedNeedsQa = signal(null)        // null=all, true=needs_qa only, false=validated only
export const selectedIgnored = signal(false)       // false=live (default), true=ignored only, null=any
export const minCountSeen = signal('')
// Date range filtered against (first_seen_at, last_seen_at) overlap.
// Defaults to "last 7 days" so the dashboard opens on actionable data
// instead of every resource ever captured.
const __today = new Date()
const __weekAgo = new Date(); __weekAgo.setDate(__today.getDate() - 7)
const __iso = d => d.toISOString().slice(0, 10)
export const dateFrom = signal(__iso(__weekAgo))
// 869eu5nuk: filter on the resource's own birth (ts_created). Empty = off.
// A split born today from May sightings lives in May by first_seen_at and
// today by ts_created; this is how "created this week" is asked.
export const createdFrom = signal('')
export const createdTo = signal('')
export const dateTo   = signal(__iso(__today))
export const brandFilter = signal('')          // partial-name search
export const freeText   = signal('')           // searches brands+cats+type+slug
// Debounced mirror of `freeText`. The reload effect depends on THIS one, never
// on `freeText` itself: the search box writes its signal on every keystroke, so
// binding the reload to it fired a full list request per character. Typing
// "rochas" meant six in-flight requests whose answers could land out of order.
export const freeTextQuery = signal('')
export const page = signal(1)
export const limit = signal(50)

export const isLoading = signal(false)

// Detail drawer state: when null, drawer is closed.
export const detailResource = signal(null)
export const detailLoading = signal(false)

// Merge mode: when true, the grid renders a checkbox on every card,
// detail-drawer-open click is replaced by toggle-selection click, and
// a floating action bar exposes the "Merge N" button. Operator turns
// it on/off explicitly from the header. The `mergeSelection` Set
// holds the currently-ticked id_resource values; never holds ids from
// different retailers (the back rejects cross-retailer merges, so we
// reset the selection when the retailer filter changes).
export const mergeMode = signal(false)
export const mergeSelection = signal(new Set())

export const clearResourcesSignals = () => batch(() => {
	retailers.value = []
	sections.value = []
	items.value = []
	counts.value = { needs_qa: 0, validated: 0 }
	total.value = 0
	selectedRetailerId.value = null
	selectedSectionId.value = null
	selectedCountry.value = ''
	selectedKind.value = null
	selectedParentKinds.value = []
	selectedIntents.value = []
	selectedCompositions.value = []
	selectedCategories.value = []
	selectedBrands.value = []
	brandFacets.value = []
	brandNames.value = {}
	brandsExpanded.value = false
	brandsUnresolved.value = 0
	selectedNeedsQa.value = null
	selectedIgnored.value = false
	minCountSeen.value = ''
	dateFrom.value = __iso(__weekAgo)
	dateTo.value   = __iso(__today)
	brandFilter.value = ''
	freeText.value = ''
	freeTextQuery.value = ''
	page.value = 1
	limit.value = 50
	isLoading.value = false
	detailResource.value = null
	detailLoading.value = false
	mergeMode.value = false
	mergeSelection.value = new Set()
})
